import React, { useState } from 'react';
import { db } from '../firebase';
import { collection, addDoc } from 'firebase/firestore';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Snackbar,
  Alert,
} from '@mui/material';
import { styled } from '@mui/system';
import CastForEducationIcon from '@mui/icons-material/CastForEducation';

// Math-related icons
import CalculateIcon from '@mui/icons-material/Calculate';
import FunctionsIcon from '@mui/icons-material/Functions';
import NumbersIcon from '@mui/icons-material/Numbers';
import PercentIcon from '@mui/icons-material/Percent';
import PlusOneIcon from '@mui/icons-material/PlusOne';
import ExposureIcon from '@mui/icons-material/Exposure';
import SquareFootIcon from '@mui/icons-material/SquareFoot';
import StraightenIcon from '@mui/icons-material/Straighten';
import TimelineIcon from '@mui/icons-material/Timeline';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import BarChartIcon from '@mui/icons-material/BarChart';
import PieChartIcon from '@mui/icons-material/PieChart';
import ScatterPlotIcon from '@mui/icons-material/ScatterPlot';
import TableChartIcon from '@mui/icons-material/TableChart';
import CloseIcon from '@mui/icons-material/Close';

// Modern color palette (consistent with other components)
const colors = {
  primary: '#2ecc71',
  secondary: '#34495e',
  background: '#f5f7fa',
  accent: '#3498db',
  gradientStart: 'rgba(62,180,137,1)',
  gradientEnd: 'rgba(144,238,144,1)',
};

// Styled components
const ModernCard = styled(Box)(({ theme }) => ({
  borderRadius: '16px',
  background: 'white',
  border: '1px solid rgba(0, 0, 0, 0.05)',
  boxShadow: '0 6px 18px rgba(0, 0, 0, 0.06)',
  padding: theme.spacing(3),
  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
  '&:hover': {
    transform: 'translateY(-6px)',
    boxShadow: '0 12px 24px rgba(0, 0, 0, 0.1)',
  },
}));

const AnimatedButton = styled(Button)(({ theme }) => ({
  borderRadius: '10px',
  textTransform: 'none',
  fontWeight: '600',
  padding: theme.spacing(1.25, 2.5),
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: colors.accent,
    color: 'white',
  },
}));

const AddSubject = () => {
  const [subject, setSubject] = useState({
    name: '',
    description: '',
    backgroundColor: '#ffffff',
    icon: '',
  });
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');

  const colorOptions = [
    { value: '#ffffff', label: 'White' },
    { value: '#f8d7da', label: 'Light Red' },
    { value: '#d4edda', label: 'Light Green' },
    { value: '#cce5ff', label: 'Light Blue' },
    { value: '#fff3cd', label: 'Light Yellow' },
    { value: '#e2e3e5', label: 'Light Gray' },
    { value: '#f5c6cb', label: 'Soft Pink' },
    { value: '#c3e6cb', label: 'Mint Green' },
    { value: '#b8daff', label: 'Sky Blue' },
    { value: '#ffeeba', label: 'Pale Yellow' },
    { value: '#d6bcfa', label: 'Lavender' },
    { value: '#fdd0e0', label: 'Baby Pink' },
    { value: '#b5f5e2', label: 'Aqua' },
    { value: '#fed7aa', label: 'Peach' },
    { value: '#e9d8fd', label: 'Lilac' },
    { value: '#a3bffa', label: 'Periwinkle' },
    { value: '#fed7d7', label: 'Coral' },
    { value: '#bef264', label: 'Lime' },
    { value: '#bbf7d0', label: 'Pistachio' },
    { value: '#bfdbfe', label: 'Cornflower Blue' },
  ];

  const iconOptions = [
    { value: 'Calculate', label: 'Calculate', component: <CalculateIcon /> },
    { value: 'Functions', label: 'Functions', component: <FunctionsIcon /> },
    { value: 'Numbers', label: 'Numbers', component: <NumbersIcon /> },
    { value: 'Percent', label: 'Percent', component: <PercentIcon /> },
    { value: 'PlusOne', label: 'Plus One', component: <PlusOneIcon /> },
    { value: 'Exposure', label: 'Arithmetic', component: <ExposureIcon /> },
    { value: 'SquareFoot', label: 'Geometry', component: <SquareFootIcon /> },
    { value: 'Straighten', label: 'Measure', component: <StraightenIcon /> },
    { value: 'Timeline', label: 'Timeline Graph', component: <TimelineIcon /> },
    { value: 'ShowChart', label: 'Line Chart', component: <ShowChartIcon /> },
    { value: 'BarChart', label: 'Bar Chart', component: <BarChartIcon /> },
    { value: 'PieChart', label: 'Pie Chart', component: <PieChartIcon /> },
    { value: 'ScatterPlot', label: 'Scatter Plot', component: <ScatterPlotIcon /> },
    { value: 'TableChart', label: 'Table Chart', component: <TableChartIcon /> },
    { value: 'Close', label: 'Multiply', component: <CloseIcon /> },
  ];

  const handleChange = (e) => {
    setSubject({ ...subject, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, 'subjects'), {
        name: subject.name,
        description: subject.description,
        backgroundColor: subject.backgroundColor,
        icon: subject.icon,
        createdAt: new Date(),
      });
      setSubject({ name: '', description: '', backgroundColor: '#ffffff', icon: '' });
      setSnackbarMessage('Subject added successfully!');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Error adding subject: ', error);
      setSnackbarMessage('Error adding subject');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbarOpen(false);
  };

  return (
    <div style={{ background: colors.background }}>
      <Container sx={{ py: 8, minHeight: '100vh', mt: 5 }}>
        {/* Header */}
        <Box
          sx={{
            mb: 5,
            p: 4,
            borderRadius: '16px',
            background: `linear-gradient(159deg, ${colors.gradientStart} 0%, ${colors.gradientEnd} 100%)`,
            color: 'white',
            boxShadow: '0 6px 18px rgba(0, 0, 0, 0.1)',
          }}
        >
          <Typography variant="h4" sx={{ fontWeight: '700', display: 'flex', alignItems: 'center' }}>
            <CastForEducationIcon sx={{ fontSize: 35, mr: 1 }} />
            Add New Subject
          </Typography>
          <Typography variant="body1" sx={{ mt: 1, color: 'rgba(255, 255, 255, 0.9)' }}>
            Create a new subject for the portal.
          </Typography>
        </Box>

        {/* Form */}
        <ModernCard sx={{ maxWidth: 600, mx: 'auto' }}>
          <form onSubmit={handleSubmit}>
            <TextField
              fullWidth
              label="Subject Name"
              name="name"
              value={subject.name}
              onChange={handleChange}
              required
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  '& fieldset': { borderColor: colors.primary },
                  '&:hover fieldset': { borderColor: colors.accent },
                  '&.Mui-focused fieldset': { borderColor: colors.accent },
                },
                '& .MuiInputLabel-root': {
                  color: colors.secondary,
                  '&.Mui-focused': { color: colors.accent },
                },
              }}
            />
            <TextField
              fullWidth
              label="Description"
              name="description"
              value={subject.description}
              onChange={handleChange}
              multiline
              rows={4}
              required
              sx={{
                mb: 2,
                '& .MuiOutlinedInput-root': {
                  borderRadius: '10px',
                  '& fieldset': { borderColor: colors.primary },
                  '&:hover fieldset': { borderColor: colors.accent },
                  '&.Mui-focused fieldset': { borderColor: colors.accent },
                },
                '& .MuiInputLabel-root': {
                  color: colors.secondary,
                  '&.Mui-focused': { color: colors.accent },
                },
              }}
            />
            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel sx={{ color: colors.secondary }}>Background Color</InputLabel>
              <Select
                name="backgroundColor"
                value={subject.backgroundColor}
                onChange={handleChange}
                sx={{
                  borderRadius: '10px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.accent },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.accent },
                }}
              >
                {colorOptions.map((color) => (
                  <MenuItem key={color.value} value={color.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          backgroundColor: color.value,
                          mr: 1,
                          border: '1px solid #ccc',
                        }}
                      />
                      {color.label}
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel sx={{ color: colors.secondary }}>Icon</InputLabel>
              <Select
                name="icon"
                value={subject.icon}
                onChange={handleChange}
                renderValue={(selected) => {
                  const selectedIcon = iconOptions.find((icon) => icon.value === selected);
                  return selectedIcon ? (
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {selectedIcon.component}
                      <Typography sx={{ ml: 1 }}>{selectedIcon.label}</Typography>
                    </Box>
                  ) : '';
                }}
                sx={{
                  borderRadius: '10px',
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: colors.primary },
                  '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: colors.accent },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: colors.accent },
                }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {iconOptions.map((icon) => (
                  <MenuItem key={icon.value} value={icon.value}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                      {icon.component}
                      <Typography sx={{ ml: 1 }}>{icon.label}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <AnimatedButton type="submit" variant="contained" color="primary">
              Add Subject
            </AnimatedButton>
          </form>
        </ModernCard>

        {/* Snackbar */}
        <Snackbar
          open={snackbarOpen}
          autoHideDuration={6000}
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert
            onClose={handleSnackbarClose}
            severity={snackbarSeverity}
            sx={{ width: '100%', bgcolor: snackbarSeverity === 'success' ? colors.primary : '#e74c3c', color: 'white' }}
          >
            {snackbarMessage}
          </Alert>
        </Snackbar>
      </Container>
    </div>
  );
};

export default AddSubject;